import { useProgressBar } from '@hooks/helpers/useProgressBar';
import styles from './styles.module.scss';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider, useWatch } from 'react-hook-form';
import { apiContactService, apiGiftCardsService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useUserContext } from '@hooks/context/useUserContext';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { createGiftCardSteps } from '@const/progress_bar_steps';
import { useEffect, useMemo, useState } from 'react';
import { ESchedulePaymentType, EFundsOperation } from '@enum';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { TContact, TCreateGiftCard, TGiftCardListItem } from '@xeppt/xeppt-sdk/types';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { EScheduleFrequency } from '@xeppt/xeppt-sdk/types/schedule';
import Button from '@components/common/button';
import Typography from '@components/common/typography';
import Recipient from '@sections/send_and_request/steps/recipient';
import Success from '@sections/send_and_request/steps/success';
import Amount from '@sections/send_and_request/steps/amount';
import Summary from '@sections/send_and_request/steps/summary';
import ProgressBar from '@components/progress_bar';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useNavigate } from 'react-router-dom';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import { getDefaultPhoneCode } from '@utils/index';

const defaultPaymentData = {
    paymentType: ESchedulePaymentType.PAYMENTS_DATE,
    contact: '',
    amount: '',
    amount_result: '',
    security_question: '',
    security_answer: '',
    isSchedule: false,
    isBulkGiftCards: false,
    giftCardRecipientEmail: '',
    phone_code: getDefaultPhoneCode(),
    phone_number: '',
    account: '',
    message: '',
    paymentFrequency: EScheduleFrequency.ONCE,
    next_payment_at: new Date(),
    number_of_payments: 1,
    expires_in: 30,
    giftCardCount: '10',
    isInfinitePayment: false,
    new_contact: {
        first_name: '',
        last_name: '',
        phone: undefined,
        email: ''
    }
};

const CreateGiftCardSection = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('sections', {
        keyPrefix: 'send_and_request.send_money'
    });
    const { submitLocale, requestErrorLocale, requestSuccessLocale } = useLocales();
    const { handleOpenActivateGiftCardModal } = useGiftCardsModals({});
    const [schedule_id] = useQueryParams(['type', 'contact_id', 'id']);
    const formMethods = useForm({
        defaultValues: defaultPaymentData
    });
    const { handleBack } = useNavigationContext();
    const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
    const { refetchAccount } = useUserContext();
    const [createdGiftCards, setCreatedGiftCards] = useState<TGiftCardListItem[]>([]);

    const steps = useMemo(() => {
        return createGiftCardSteps;
    }, [createGiftCardSteps]);

    const { availableStep, prevStep, nextStep, currentStep, changeStep } = useProgressBar(steps, [
        formMethods.watch()
    ]);

    const handleNextStep = (onSuccess: () => void) => {
        formMethods.trigger().then((isValid) => {
            if (isValid) {
                onSuccess();
            }
        });
    };

    const { handleRequest: handleCreateGiftCard } = useApiMutation({
        method: () => {
            const values = formMethods.getValues();
            const request: TCreateGiftCard[] = [];
            if (values?.isBulkGiftCards) {
                Array(Number(values?.giftCardCount))
                    .fill(' ')
                    .forEach((_, i) => {
                        request.push({
                            amount: Number(values.amount),
                            description: values.message,
                            email: recipientEmails?.[i] || undefined,
                            currency: 'CAD'
                        });
                    });
            } else {
                request.push({
                    amount: Number(values.amount),
                    description: values.message,
                    email: values?.giftCardRecipientEmail || undefined,
                    phone_code:
                        values?.phone_code && values?.phone_number ? values?.phone_code : undefined,
                    phone_number:
                        values?.phone_code && values?.phone_number
                            ? values?.phone_number
                            : undefined,
                    currency: 'CAD'
                });
            }
            return apiGiftCardsService.createGiftCard(request);
        },
        onSuccess: () => {
            const values = formMethods.getValues();
            requestSuccessLocale(
                values?.isBulkGiftCards ? 'gift_cards_create_success' : 'gift_card_create_success'
            );
            nextStep();
            refetchAccount();
        },
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        handleCreateGiftCard(undefined);
    };

    const renderContent = () => {
        switch (currentStep?.id) {
            case '1':
                return (
                    <Amount
                        isEdit={false}
                        onPrev={prevStep}
                        contactList={[]}
                        method={EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD}
                        refetchFeeTrigger={0}
                        isCustomContact={false}
                        onSubmit={() => handleNextStep(nextStep)}
                    />
                );
            case '2':
                return (
                    <Recipient
                        onSubmit={() => handleNextStep(nextStep)}
                        contactsList={[]}
                        method={EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD}
                        onPrev={prevStep}
                        onChangeNewContact={() => {}}
                        onChangeCustomContact={() => {}}
                        onChangeEmails={setRecipientEmails}
                    />
                );
            case '3':
                return (
                    <Summary
                        isEdit={false}
                        onPrev={prevStep}
                        onSubmit={onSubmit}
                        contactList={[]}
                        isLoading={false}
                        method={EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD}
                        isCustomContact={false}
                        giftCardEmails={recipientEmails}
                    />
                );
            case '4':
                return (
                    <Success
                        onReset={() => {
                            formMethods.reset(defaultPaymentData);
                            changeStep('1');
                            navigate(routes.gift_cards_create);
                            // handleOpenActivateGiftCardModal(createdGiftCards)
                        }}
                        isSchedule={false}
                        method={EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD}
                        isExternal={false}
                    />
                );
        }
    };

    useEffect(() => {
        scrollTo(0, 0);
    }, [currentStep]);

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => {
                        if (currentStep?.id !== '1') {
                            prevStep();
                        } else {
                            handleBack(routes.gift_cards);
                        }
                    }}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Create gift card</Typography>
            </div>
            <div className={styles.content_wrapper}>
                <ProgressBar
                    disabled={!!schedule_id}
                    steps={steps}
                    availableStep={availableStep}
                    currentStep={currentStep}
                    className={styles.progress}
                    changeStep={changeStep}
                />
                <div className={styles.body_wrapper}>
                    <FormProvider {...formMethods}>{renderContent()}</FormProvider>
                </div>
            </div>
        </motion.div>
    );
};

export default CreateGiftCardSection;
